import { Fragment } from 'react/jsx-runtime';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import type { ClientRole } from '@/api/roles-and-permission';
import { CheckboxCell } from '@/features/Settings/RolesAndPermission/CheckboxCell';
import { SelectAll } from '@/features/Settings/RolesAndPermission/SelectAll';
import type { PrevilegesForm, TProductGroupType } from '@/features/Settings/RolesAndPermission/utils';
import type { GroupedPermission, PermissionGroup } from '@/types/Permission';

export function PermissionGrid({
  currentNav,
  permissions,
  permissionKeys,
  clientRoles,
  allChecked,
  allUnchecked,
}: {
  currentNav: TProductGroupType;
  permissions?: GroupedPermission;
  permissionKeys: PermissionGroup[];
  clientRoles?: ClientRole[];
  allChecked: PrevilegesForm;
  allUnchecked: PrevilegesForm;
}) {
  const { control, getValues, setValue } = useFormContext<PrevilegesForm>();

  return (
    <div className='relative z-10 overflow-auto h-[calc(100dvh-372px)] border border-t-0'>
      <table className='w-full border-separate border-spacing-0 bg-gray-100'>
        <colgroup>
          <col className='min-w-[400px]'></col>
          {clientRoles?.map((role) => (
            <col key={role.slug} className='min-w-[96px]'></col>
          ))}
        </colgroup>
        <tbody>
          {permissionKeys
            ?.filter((key) => !!permissions?.[key])
            ?.map((key, index) => (
              <Fragment key={key}>
                {index === 0 && (
                  <tr className='bg-gray-100 z-10'>
                    <th className='text-left relative z-100 px-4 py-2 sticky top-0 left-0 border-r border-t bg-gray-100 text-xs'>
                      Apply All
                    </th>

                    {clientRoles?.map((role) => (
                      <SelectAll
                        control={control}
                        getValues={getValues}
                        setValue={setValue}
                        currentNav={currentNav}
                        role={role}
                        key={role.slug}
                        allChecked={allChecked}
                        allUnchecked={allUnchecked}
                      />
                    ))}
                  </tr>
                )}

                <tr className='bg-gray-100 z-10'>
                  <th className='text-left relative z-30 px-4 py-4 sticky top-[35px] left-0 border-r border-y bg-gray-100'>{key}</th>

                  {index === 0
                    ? clientRoles?.map((role) => (
                        <th key={role.slug} className='text-center px-4 py-1 sticky top-[35px] bg-gray-100 border-y z-20'>
                          {role.name}
                        </th>
                      ))
                    : clientRoles?.map((_role, index) => <th key={index} className='border-y'></th>)}
                </tr>

                {permissions?.[key]?.map(({ slug, name, description = '-' }, permissionIndex) => (
                  <tr key={`${key}-${slug}`} className='bg-white hover:bg-gray-50 group'>
                    <td
                      className={twMerge([
                        'p-2 pl-4 pr-5 sticky left-0 bg-inherit border-r border-t z-20',
                        permissionIndex === 0 && 'border-t-0',
                      ])}
                    >
                      <p className='text-md mb-1'>{name}</p>
                      <p className='text-xs text-gray-400'>{description}</p>
                    </td>
                    {clientRoles?.map((role) => {
                      return (
                        <CheckboxCell
                          key={`${role.slug}.${key}.${slug}`}
                          role={role}
                          slug={slug}
                          permissionKey={key}
                          permissionIndex={permissionIndex}
                          control={control}
                          getValues={getValues}
                          setValue={setValue}
                          currentNav={currentNav}
                        />
                      );
                    })}
                  </tr>
                ))}
              </Fragment>
            ))}
        </tbody>
      </table>
    </div>
  );
}
